import { useState, useCallback } from "react";
import { Check, Eye, Loader2, MoreVertical, Plus, RotateCcw, Settings2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SectionGrid } from "@/pages/admin/dashboards/SectionGrid";
import { DashboardFilterProvider } from "@/contexts/DashboardFilterContext";
import type { Dashboard, DashboardSection } from "@/types/dashboard";
import type { WidgetPlacement } from "@/types/widget";

export function DashboardEditorPanel({
  config,
  sortedSections,
  onSave,
  addSection,
  updateSection,
  deleteSection,
  moveSectionUp,
  moveSectionDown,
  updateSectionPlacements,
  addWidgetToSection,
  renameWidget,
  duplicateWidget,
  removeWidget,
  moveWidgetToSection,
  onSettings,
  onPreview,
  onReset,
  onExitEdit,
}: {
  config: Dashboard;
  sortedSections: DashboardSection[];
  onSave: () => Promise<void>;
  // Section operations
  addSection: () => void;
  updateSection: (id: string, patch: Partial<Pick<DashboardSection, "name" | "collapsed">>) => void;
  deleteSection: (id: string) => void;
  moveSectionUp: (id: string) => void;
  moveSectionDown: (id: string) => void;
  updateSectionPlacements: (id: string, placements: WidgetPlacement[]) => void;
  // Widget operations
  addWidgetToSection: (sectionId: string, widgetId: string) => void;
  renameWidget: (sectionId: string, instanceId: string, title: string) => void;
  duplicateWidget: (sectionId: string, instanceId: string) => void;
  removeWidget: (sectionId: string, instanceId: string) => void;
  moveWidgetToSection: (sectionId: string, instanceId: string, toSectionId: string) => void;
  // Other actions
  onSettings?: () => void;
  onPreview?: () => void;
  onReset?: () => void;
  onExitEdit?: () => void;
}) {
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved">("idle");
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);

  const handleSave = useCallback(async () => {
    setSaveState("saving");
    try {
      await onSave();
      setSaveState("saved");
      setTimeout(() => setSaveState("idle"), 1500);
    } catch {
      setSaveState("idle");
    }
  }, [onSave]);

  function getSiblings(currentSectionId: string) {
    return sortedSections
      .filter((s) => s.id !== currentSectionId)
      .map((s) => ({ id: s.id, name: s.name }));
  }

  const hasSecondaryActions = onSettings || onPreview || onExitEdit;

  return (
    <div className="w-full flex flex-col gap-4 sm:gap-6 py-4 sm:py-6">
      <ConfirmDialog
        open={resetConfirmOpen}
        onOpenChange={setResetConfirmOpen}
        variant="destructive"
        title="Reset to default?"
        description="Your layout customization will be removed. The dashboard will revert to the default configuration."
        confirmLabel="Reset"
        onConfirm={() => {
          onReset?.();
          onExitEdit?.();
        }}
      />
      {/* Action bar */}
      <div className="flex flex-wrap items-center gap-2 px-1">
        <h2 className="text-xl font-semibold text-primary flex-1 min-w-0 truncate">
          {config.name}
        </h2>

        {hasSecondaryActions && (
          <div className="flex items-center shrink-0">
            {onSettings && (
              <Button
                variant="ghost"
                onClick={onSettings}
                className="gap-1.5"
                title="Dashboard settings"
              >
                <Settings2 className="h-4 w-4" />
                <span className="hidden sm:inline">Settings</span>
              </Button>
            )}
            {onPreview && (
              <Button variant="ghost" onClick={onPreview} className="gap-1.5">
                <Eye className="h-4 w-4" />
                <span className="hidden sm:inline">Preview</span>
              </Button>
            )}
            {onExitEdit && (
              <Button variant="ghost" onClick={onExitEdit} className="gap-1.5">
                <X className="h-4 w-4" />
                <span className="hidden sm:inline">Done</span>
              </Button>
            )}
          </div>
        )}

        <Button
          onClick={handleSave}
          className="shrink-0 gap-1.5 min-w-18"
          disabled={saveState === "saving"}
        >
          {saveState === "saving" && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          {saveState === "saved" && <Check className="h-3.5 w-3.5" />}
          {saveState === "saving" ? "Saving…" : saveState === "saved" ? "Saved" : "Save"}
        </Button>

        {onReset && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="shrink-0">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                className="gap-2 text-status-danger"
                onClick={() => setResetConfirmOpen(true)}
              >
                <RotateCcw className="h-4 w-4" />
                Reset to default
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Sections */}
      <DashboardFilterProvider>
        <div className="flex flex-col gap-6">
          {sortedSections.map((section, idx) => (
            <SectionGrid
              key={section.id}
              section={section}
              mode="edit"
              siblingeSections={getSiblings(section.id)}
              isFirst={idx === 0}
              isLast={idx === sortedSections.length - 1}
              onUpdateSection={(patch) => updateSection(section.id, patch)}
              onDeleteSection={() => deleteSection(section.id)}
              onMoveUp={() => moveSectionUp(section.id)}
              onMoveDown={() => moveSectionDown(section.id)}
              onPlacementsChange={(placements) => updateSectionPlacements(section.id, placements)}
              onAddWidget={(widgetId) => addWidgetToSection(section.id, widgetId)}
              onRenameWidget={(instanceId, title) => renameWidget(section.id, instanceId, title)}
              onDuplicateWidget={(instanceId) => duplicateWidget(section.id, instanceId)}
              onRemoveWidget={(instanceId) => removeWidget(section.id, instanceId)}
              onMoveWidgetToSection={(instanceId, toSectionId) =>
                moveWidgetToSection(section.id, instanceId, toSectionId)
              }
            />
          ))}

          <button
            onClick={() => addSection()}
            className="w-full py-4 rounded-lg border border-dashed border-border/50 text-sm text-muted hover:border-brand-cyan/40 hover:text-brand-cyan transition-colors flex items-center justify-center gap-2 cursor-pointer"
          >
            <Plus className="h-4 w-4" />
            Add Section
          </button>
        </div>
      </DashboardFilterProvider>
    </div>
  );
}
